import React, { useMemo } from 'react';
import { Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { catchyTextFieldSx } from '../../theme/formStyles';

export interface DescriptionOption {
  _id?: string;
  name: string;
  category?: string;
  inputValue?: string;
}

interface CreatableDescriptionSelectProps {
  value: string;
  options: DescriptionOption[];
  onChange: (value: string) => void;
  onCreate?: (name: string) => Promise<DescriptionOption | null>;
  disabled?: boolean;
}

const filter = createFilterOptions<DescriptionOption>();

export const CreatableDescriptionSelect: React.FC<CreatableDescriptionSelectProps> = ({
  value,
  options,
  onChange,
  onCreate,
  disabled,
}) => {
  const selected = useMemo(
    () => options.find((o) => o.name === value) || (value ? { name: value } : null),
    [options, value]
  );

  return (
    <Autocomplete
      freeSolo
      size="small"
      disabled={disabled}
      options={options}
      value={selected}
      inputValue={value}
      onInputChange={(_e, inputValue, reason) => {
        if (reason === 'input') onChange(inputValue);
      }}
      onChange={async (_e, option) => {
        if (!option) {
          onChange('');
          return;
        }
        if (typeof option === 'string') {
          onChange(option);
          return;
        }
        if (option.inputValue) {
          const created = onCreate ? await onCreate(option.inputValue) : null;
          onChange(created?.name || option.inputValue);
          return;
        }
        onChange(option.name);
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        if (option.inputValue) return option.inputValue;
        return option.name;
      }}
      isOptionEqualToValue={(opt, val) => opt.name === val.name}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const input = params.inputValue.trim();
        if (input && !opts.some((o) => o.name.toLowerCase() === input.toLowerCase())) {
          filtered.push({ name: `Add "${input}"`, inputValue: input });
        }
        return filtered;
      }}
      renderOption={(props, option) => (
        <li {...props} key={option._id || option.inputValue || option.name}>
          {option.inputValue ? (
            <strong>{option.name}</strong>
          ) : (
            <div>
              <strong>{option.name}</strong>
              {option.category && (
                <span style={{ display: 'block', fontSize: 12, opacity: 0.75 }}>{option.category}</span>
              )}
            </div>
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Choose or type description"
          sx={catchyTextFieldSx}
        />
      )}
      noOptionsText="Type to add description"
      clearOnEscape
    />
  );
};
